'use client'

import {useEffect, useEffectEvent, useRef, useState, type ReactNode} from 'react'
import type {PublicScheduleEvent} from '../../../services/public-schedule.service'
import {parseBenefitCardKey} from '@fitro/customer-ui/BenefitProvider'
import {PublicGuestBookingChooser} from './PublicGuestBookingChooser'
import {PublicGuestCheckoutPanel} from './PublicGuestCheckoutPanel'
import {publicEventPrice} from './publicEventPrice'
import {useEventGuestCheckout} from './useEventGuestCheckout'
import {isEmbedSourceVisit} from './publicEntryLinks'
import {clearPublicEventGuestCheckoutRequest, isPublicEventGuestCheckoutRequested} from './publicEventLinks'

/**
 * Booking a Term without an account on its page: the guest form replaces the button, then the
 * payment redirect. Only a benefit card price or a refused preview opens a choice in a dialog first;
 * a refusal after the form was sent stays in the form. Leaving the form drops it; a Hold already
 * taken stays reachable from its email.
 * A visit that already asked to book starts at once: a guest link goes straight to the form when
 * there is no card to choose, a website widget visit shows the choice.
 */
export function PublicGuestBooking({
    children,
    className,
    event,
    partnerSlug,
    signInHref,
}: {
    children: ReactNode
    className: string
    event: PublicScheduleEvent
    partnerSlug: string
    signInHref: string
}) {
    const oneOff = event.isCyclical === false
    const otherTermsHref = oneOff ? '/wydarzenia' : '/'
    const checkout = useEventGuestCheckout(event, partnerSlug)
    const entryPrice = publicEventPrice(event)
    const [opened, setOpened] = useState(false)
    const arrivalHandled = useRef(false)
    const arrive = useEffectEvent(() => {
        if (arrivalHandled.current) return
        arrivalHandled.current = true
        const requested = isPublicEventGuestCheckoutRequested(window.location.search)
        if (!requested && !isEmbedSourceVisit()) return
        const straightToForm = requested && checkout.cards.length === 0
        if (straightToForm) void checkout.start()
        // The form replaces the button on the page, so only the choice opens a dialog.
        else setOpened(true)
    })
    useEffect(() => { arrive() }, [])

    const form = checkout.preview ? (
        <PublicGuestCheckoutPanel
            partnerSlug={partnerSlug}
            preview={checkout.preview}
            benefitProvider={checkout.cardKey ? parseBenefitCardKey(checkout.cardKey)?.provider : null}
            onCancel={() => {
                clearPublicEventGuestCheckoutRequest()
                checkout.cancel()
            }}
            onRenew={checkout.renew}
            otherTermsHref={otherTermsHref}
            signInHref={signInHref}
            waitlistAvailable={event.waitlistAvailable === true}
            firstEntryMinor={entryPrice?.kind === 'paid' ? entryPrice.firstEntryMinor : null}
        />
    ) : null
    if (form) return form

    return (
        <PublicGuestBookingChooser
            benefitCardKey={checkout.cardKey}
            className={className}
            defaultOpen={opened || checkout.failure !== null}
            event={event}
            failure={checkout.failure}
            direct={checkout.cards.length === 0}
            mode="guest"
            onBenefitCardChange={checkout.setCardKey}
            onClose={() => {
                setOpened(false)
                clearPublicEventGuestCheckoutRequest()
                checkout.cancel()
            }}
            onGuest={() => void checkout.start()}
            otherTermsHref={otherTermsHref}
            pending={checkout.previewing}
            signInHref={signInHref}
        >
            {children}
        </PublicGuestBookingChooser>
    )
}
